import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CrosshairIcon,
  ExpandIcon,
  type LucideIcon,
  RotateCcwIcon,
  ZoomInIcon,
  ZoomOutIcon
} from 'lucide-react';
import { useCallback, useEffect, useEffectEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TransformComponent,
  TransformWrapper,
  useControls,
  useTransformComponent
} from 'react-zoom-pan-pinch';

import { Dialog, DialogContent, DialogTitle } from '@r/components/ui/dialog.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@r/components/ui/tooltip.tsx';
import { cn } from '@r/utils/utils.ts';

const ZOOM_STEP = 0.4;
const ANIMATION_MS = 200;
const ANIMATION_EASE = 'easeOut' as const;

interface MediaViewerProps {
  /** URLs des médias (images). */
  media: string[];
  alt?: string;
  /**
   * Classe de l'aperçu inline : posée sur l'`<img>` en mode mono-média,
   * sur le conteneur du rail en mode multi-média.
   */
  className?: string;
}

/** Bouton d'outil du viewer, avec tooltip. */
function ToolButton({
  icon: Icon,
  label,
  onClick
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          className="rounded-lg p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
        >
          <Icon className="size-4" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        sideOffset={8}
        className="border-white/10 bg-black/85 px-2 py-1 text-xs font-medium text-white shadow-lg"
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

/** Barre d'outils zoom — consomme le contexte via `useControls` (recommandation lib). */
function ViewerToolbar() {
  const { t } = useTranslation();
  const { zoomIn, zoomOut, resetTransform, centerView } = useControls();

  return (
    <div
      role="toolbar"
      aria-label={t('media.viewer.toolbar')}
      className="absolute left-1/2 top-4 flex -translate-x-1/2 items-center gap-0.5 rounded-xl border border-white/10 bg-black/60 p-1 shadow-lg backdrop-blur"
    >
      <ToolButton
        icon={ZoomInIcon}
        label={t('media.viewer.zoom.in')}
        onClick={() => zoomIn(ZOOM_STEP, ANIMATION_MS, ANIMATION_EASE)}
      />
      <ToolButton
        icon={ZoomOutIcon}
        label={t('media.viewer.zoom.out')}
        onClick={() => zoomOut(ZOOM_STEP, ANIMATION_MS, ANIMATION_EASE)}
      />
      <span className="mx-1 h-4 w-px bg-white/15" />
      <ToolButton
        icon={RotateCcwIcon}
        label={t('media.viewer.zoom.reset')}
        onClick={() => resetTransform(ANIMATION_MS, ANIMATION_EASE)}
      />
      <ToolButton
        icon={CrosshairIcon}
        label={t('media.viewer.zoom.center')}
        onClick={() => centerView(undefined, ANIMATION_MS, ANIMATION_EASE)}
      />
    </div>
  );
}

/** Badge d'échelle temps réel — s'abonne à l'état via `useTransformComponent`. */
function ScaleBadge() {
  const scale = useTransformComponent(({ state }) => state.scale);

  return (
    <div className="pointer-events-none absolute bottom-4 right-4 z-10 rounded-md border border-white/10 bg-black/70 px-2.5 py-1 text-[11px] font-semibold tabular-nums tracking-wide text-white/80 backdrop-blur">
      {scale.toFixed(2)}×
    </div>
  );
}

/**
 * Affiche un média (ou plusieurs) avec ouverture plein écran zoom / pan / pinch.
 * Actuellement les appelants ne passent qu'un média ; le chemin multi-média
 * (miniatures + navigation) reste en veille jusqu'à un usage réel (produits boutique).
 */
export function MediaViewer({ media, alt = '', className }: MediaViewerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  const count = media.length;
  const hasMultiple = count > 1;

  const openAt = useCallback((position: number) => {
    setIndex(position);
    setOpen(true);
  }, []);

  const go = useCallback(
    (delta: number) => setIndex((current) => (current + delta + count) % count),
    [count]
  );

  // Non réactif : ne doit pas re-déclencher l'abonnement clavier quand `go` change
  // d'identité (ex. si `count` varie pendant que la modale est ouverte).
  const onNavigate = useEffectEvent((delta: number) => go(delta));

  useEffect(() => {
    if (!open || !hasMultiple) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') onNavigate(-1);
      if (event.key === 'ArrowRight') onNavigate(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, hasMultiple]);

  if (count === 0) return null;

  return (
    <>
      {hasMultiple ? (
        <div className={cn('flex flex-wrap gap-2', className)}>
          {media.map((src, position) => (
            <button
              key={src}
              type="button"
              onClick={() => openAt(position)}
              className="overflow-hidden rounded-lg border"
              aria-label={t('media.viewer.open')}
            >
              <img src={src} alt={alt} loading="lazy" className="size-16 object-cover" />
            </button>
          ))}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => openAt(0)}
          className={cn('group relative block cursor-zoom-in overflow-hidden', className)}
          aria-label={t('media.viewer.open')}
        >
          <img src={media[0]} alt={alt} loading="lazy" className={cn('block', className)} />
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition group-hover:bg-black/20 group-hover:opacity-100">
            <ExpandIcon className="size-6 text-white drop-shadow" />
          </span>
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="h-[min(85vh,760px)] w-[min(92vw,1024px)] max-w-none gap-0 overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a14] p-0 [&>button]:text-white/70 [&>button:hover]:text-white"
        >
          <DialogTitle className="sr-only">{alt || t('media.viewer.title')}</DialogTitle>

          <TooltipProvider delayDuration={300}>
            <TransformWrapper
              key={index}
              centerOnInit
              minScale={0.5}
              maxScale={8}
              doubleClick={{
                mode: 'toggle',
                animationTime: ANIMATION_MS,
                animationType: ANIMATION_EASE
              }}
            >
              <TransformComponent
                wrapperClass="!h-full !w-full"
                contentClass="!flex !h-full !w-full !items-center !justify-center"
              >
                <img
                  src={media[index]}
                  alt={alt}
                  className="max-h-full max-w-full object-contain"
                />
              </TransformComponent>

              <ViewerToolbar />
              <ScaleBadge />
            </TransformWrapper>
          </TooltipProvider>

          {hasMultiple && (
            <>
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label={t('media.viewer.previous')}
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/10 bg-black/60 p-2 text-white/80 shadow backdrop-blur transition hover:bg-black/80 hover:text-white"
              >
                <ChevronLeftIcon className="size-5" />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label={t('media.viewer.next')}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/10 bg-black/60 p-2 text-white/80 shadow backdrop-blur transition hover:bg-black/80 hover:text-white"
              >
                <ChevronRightIcon className="size-5" />
              </button>
              <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5">
                {media.map((src, position) => (
                  <button
                    key={src}
                    type="button"
                    onClick={() => setIndex(position)}
                    aria-label={t('media.viewer.goto', { number: position + 1 })}
                    className={cn(
                      'size-2 rounded-full transition',
                      position === index ? 'bg-white' : 'bg-white/30'
                    )}
                  />
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
